import { FC, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
// icons
import PauseSVG from 'assets/image/pause.svg';
import PlaySVG from 'assets/image/play.svg';
import ButtonTrash from 'shared/components/Buttons/ButtonTrash';

// components
import EditVolunteerAccount from 'components/Organization/Volunteers/VolunteersPopups/EditVolunteerAccount';
import ButtonPencil from 'shared/components/Buttons/ButtonPencil';
import PopupContainer from 'shared/components/PopupContainer';
import ButtonSuspending from 'shared/components/Buttons/ButtonSuspending';
// types
import { IBasicVolunteerResponse, VOLUNTEER_ACTIVE_STATUS } from '@joc/api-gateway';
import { deleteVolunteer, updateActiveStatus } from 'redux/volunteer-service/actions';
import { VolunteerTableDataContext } from 'components/Organization/Volunteers/Table';
import { VolunteersPageContext } from 'pages/Organization/Volunteers';
// styles
import styles from './VolunteerActionCell.module.scss';

type VolunteerListRecord = IBasicVolunteerResponse & { userId?: string };

const VolunteerActionCell: FC = () => {
	const dispatch = useDispatch();

	const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const volunteerData: VolunteerListRecord = useContext(VolunteerTableDataContext);
	const { refreshVolunteers } = useContext(VolunteersPageContext);

	const isVolunteerSuspended = useMemo(
		() =>
			Object.hasOwn(volunteerData?.volunteerActiveStatus, 'status') &&
			volunteerData.volunteerActiveStatus.status === VOLUNTEER_ACTIVE_STATUS.SUSPENDED,
		[volunteerData]
	);

	const handleChangeVolunteerStatus = () =>
		dispatch(
			updateActiveStatus({
				volunteerId: volunteerData.id,
				status: isVolunteerSuspended ? VOLUNTEER_ACTIVE_STATUS.ACTIVE : VOLUNTEER_ACTIVE_STATUS.SUSPENDED,
			})
		);

	const handleDeleteVolunteer = async () => {
		if (!volunteerData.id || isDeleting) return;

		setIsDeleting(true);
		try {
			// List `id` is volunteer id. userId is often missing on search rows (gateway fromJS strips it);
			// deleteVolunteer resolves it via getVolunteerById when needed.
			await (dispatch(deleteVolunteer(volunteerData.id, volunteerData.userId)) as unknown as Promise<void>);
			refreshVolunteers?.();
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<div>
			<div className={styles.actions__content}>
				<ButtonPencil clickHandler={() => setIsShowPopup(true)} />
				<ButtonSuspending
					clickHandler={handleChangeVolunteerStatus}
					title="suspend volunteer"
					href={isVolunteerSuspended ? PlaySVG : PauseSVG}
				/>
				{isVolunteerSuspended ? (
					<ButtonTrash clickHandler={handleDeleteVolunteer} title="delete volunteer" />
				) : null}
			</div>
			{isShowPopup && (
				<PopupContainer setIsShowPopup={setIsShowPopup}>
					<EditVolunteerAccount setIsShowPopup={setIsShowPopup} />
				</PopupContainer>
			)}
		</div>
	);
};

export default VolunteerActionCell;
